<script setup>
// import { useRouter } from "vue-router";
import { useHead } from "@vueuse/head";
import CtaSection from "@/components/molecules/CtaSection.vue";

// const router = useRouter();

// const navigateTo = (path) => {
//   router.push(path);
// };

// SEO構造化データ（Article スキーマ）
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "配信クラファンとは?概要・メリットをわかりやすく解説",
  description:
    "配信クラファンって何?通常のクラファンと違うの?概要・参加のメリットをわかりやすく解説。",
  datePublished: "2025-10-27",
  dateModified: "2025-10-27",
  author: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  publisher: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  image: "/img/airticlepic/article1.webp",
};

// メタ情報と構造化データを設定
useHead({
  title: "配信クラファンとは?概要・メリットをわかりやすく解説 | SukiSukiらいぶすてーしょん",
  meta: [
    {
      name: "description",
      content:
        "配信クラファンって何?通常のクラファンと違うの?概要・参加のメリットをわかりやすく解説。",
    },
    {
      property: "og:title",
      content: "配信クラファンとは?概要・メリットをわかりやすく解説 | SukiSukiらいぶすてーしょん",
    },
    {
      property: "og:description",
      content:
        "配信クラファンって何?通常のクラファンと違うの?概要・参加のメリットをわかりやすく解説。",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: "https://sukisuki.live/img/airticlepic/article1.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(articleSchema),
    },
  ],
});
</script>

<template>
  <v-app>
    <!-- ヒーローセクション -->
    <figure class="hero-section">
      <img src="/img/airticlepic/article1.webp" alt="配信クラファンの概要を解説するヒーロー画像" />
    </figure>

    <!-- パンくずナビゲーション -->
    <nav aria-label="パンくずリスト" class="breadcrumb-section">
      <v-container>
        <ol class="breadcrumb">
          <li><a href="/academy">TOP</a></li>
          <li>配信クラファン</li>
        </ol>
      </v-container>
    </nav>

    <!-- メインコンテンツ -->
    <main>
      <article class="article-content">
        <v-container>
          <!-- 記事タイトル -->
          <div class="article-title-box">
            <h1>配信クラファンとは？概要・メリットをわかりやすく解説</h1>
          </div>

          <!-- 更新日 -->
          <div class="update-date-box">
            <time datetime="2025-10-27">更新日:2025年10月27日</time>
          </div>

          <!-- イントロダクション -->
          <div class="intro-box">
            <p>
              配信クラファンって何？通常のクラファンと違うの？概要・参加のメリットをわかりやすく解説。
            </p>
          </div>

          <section class="learn-section">
            <h2 class="learn-title">この記事でわかる内容</h2>
            <div class="learn-content">
              <p>
                「配信クラファン」という言葉を耳にしたことはありますか？
                クラウドファンディングとライブ配信を掛け合わせた新しい支援の形として、クリエイター・ライバー・VTuberの間で注目を集めています。
              </p>
              <p>
                この記事では、配信クラファンの基本的な仕組みから、通常のクラファンとの違い、そして配信者・ファン双方にとってのメリットまでをわかりやすく解説します。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">クラウドファンディングとは？</h2>
            <div class="learn-content">
              <p>
                クラウドファンディング（クラファン）とは、インターネットを通じて不特定多数の人から資金を募る仕組みのことです。個人や企業がプロジェクトを立ち上げ、支援者がその活動を応援することで成り立ちます。
                クラファンには主に以下の3つの形式があります。
              </p>
              <p>
                <span class="bold-text">1.購入型：</span
                >支援金に応じてリターン（商品や限定グッズ）を受け取る形式
              </p>
              <p>
                <span class="bold-text">2.寄付型：</span
                >リターンを伴わず、活動そのものを支援する形式
              </p>
              <p>
                <span class="bold-text">3.投資型：</span
                >リターンとして金銭的リターン（分配など）を得る形式
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">配信クラファンとは？</h2>
            <div class="learn-content">
              <p>
                「配信クラファン」とは、ライブ配信を通じて行うクラウドファンディングを指します。
                配信者がリアルタイムでファンに企画を紹介し、視聴者がその場で支援できる仕組みです。
              </p>
              <p>たとえば、次のような目標があります：</p>
              <p>
                <span class="bold-text">・「新しい楽曲を制作したい」</span>
              </p>
              <p>
                <span class="bold-text">・「3Dモデル制作の費用を集めたい」</span>
              </p>
              <p>
                <span class="bold-text">・「オリジナルライブイベントを開催したい」</span>
              </p>
              <p>といった目標を、ライブ配信中に説明・発表しながら支援を募ることが可能です。</p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">配信クラファンの流れ</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">1.配信者がプロジェクトを立ち上げる</span><br />
                プロジェクトの目的・目標金額・リターン内容などを設定。
              </p>
              <p>
                <span class="bold-text">2.ライブ配信で企画を紹介</span><br />
                配信を通してリターンや活動の背景をリアルタイムで説明。※配信時間外でも支援購入は可能です。
              </p>
              <p>
                <span class="bold-text">3. 視聴者がその場で支援</span><br />
                支援ボタンやリンクを通じて、コメントしながら支援購入が可能。
              </p>
              <p>
                <span class="bold-text">4.目標達成後、リターン提供へ</span><br />
                リターンとしてオリジナルグッズ制作などを実施。
              </p>
              <p>
                <span class="bold-text">5.プロジェクト実施</span><br />
                集まった支援金をもとに、プロジェクトを実施。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">通常のクラファンとの違い</h2>
            <div class="learn-content-table">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>比較項目</th>
                    <th>通常のクラファン</th>
                    <th>配信クラファン</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>プロモーション</td>
                    <td>ページやSNSで静的に紹介</td>
                    <td>配信でリアルタイムに紹介・反応</td>
                  </tr>
                  <tr>
                    <td>支援タイミング</td>
                    <td>ページを見て支援</td>
                    <td>配信を見ながら支援</td>
                  </tr>
                  <tr>
                    <td>支援動機</td>
                    <td>内容に共感して支援</td>
                    <td>感情・熱量に共感して支援</td>
                  </tr>
                  <tr>
                    <td>コミュニケーション</td>
                    <td>コメントや更新</td>
                    <td>ライブ中のやりとりが可能</td>
                  </tr>
                  <tr>
                    <td>熱量・臨場感</td>
                    <td>蓄積型</td>
                    <td>一体感・瞬発力が高い</td>
                  </tr>
                </tbody>
              </table>
              従来のクラファンが「情報を届ける」仕組みなら、
              配信クラファンは「一緒に体験する」仕組みです。
              支援者は観客ではなく、"配信を通じて共に夢を叶える仲間"という感覚で参加できます。
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">SukiSukiらいぶすてーしょんの配信クラファンならではの特徴</h2>
            <div class="learn-content">
              <p>
                SukiSukiらいぶすてーしょんでは、配信クラファンをもっと手軽に、もっとクリエイティブに実現できるよう、すべての制作・運営をワンストップでサポートしています。
              </p>
              <p>
                <span class="bold-text"
                  >💡 企画立案・リターン設定・ページ制作をすべて代行（無料）</span
                >
              </p>
              <p>
                通常のクラファンで大きな負担となる部分を、SukiSuki運営チームが丸ごと対応します。クリエイターは配信内容の構想やファンとの関係づくりに集中できます。
              </p>
              <p>
                <span class="bold-text">🎨 支援金を使ってリターングッズを制作・発送まで代行</span>
              </p>
              <p>
                集まった支援金をもとに、SukiSukiがデザインから製造・発送までワンストップで完結。
                ご準備いただくのは「デザイン素材」だけです。
              </p>
              <p>
                <span class="bold-text">👤 分からないことは何でも相談できる</span>
              </p>
              <p>
                企画立案から配信方法・リターン設計まで、何でも専属の担当と相談ができます。連絡方法は、LINEやdiscordやメールなど、お好きな方法でご相談ください。
              </p>
              <p>
                こうした仕組みにより、企画準備の手間やページ制作の知識がなくても、
                誰でもすぐに「配信クラファン」に挑戦できるのがSukiSukiらいぶすてーしょんの最大の強みです。
              </p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <CtaSection article-id="academy-article-1" />

          <section class="learn-section">
            <h2 class="learn-title">配信者にとってのメリット</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">①ファンとの距離が近く、共感を生みやすい</span>
              </p>
              <p>
                ライブ配信を通じて、支援者に直接想いや背景を伝えられます。
                コメントへの反応や即時のリアクションが、共感を呼び支援を後押しします。
              </p>
              <p>
                <span class="bold-text">②支援購入の瞬間、配信が湧き、その場でお礼が言える</span>
              </p>
              <p>
                ライブ配信中に支援購入が可能で、支援いただいた瞬間、お礼をリアルタイムで視聴者の方に伝えることができます。
                また、SukiSukiらいぶすてーしょんでは企画構築から配信当日までサポートするため、初めてでも安心です。
              </p>
              <p>
                <span class="bold-text">③グッズ制作のハードルが低い</span>
              </p>
              <p>
                支援金を活用してリターングッズを制作できるため、金銭的リスクを抱えずに挑戦可能です。
                さらに、制作・発送を代行してもらえるため、配信後も運営負担がほとんどありません。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">ファンにとってのメリット</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">①"リアルタイムで叶える"感動がある</span>
              </p>
              <p>
                支援がその場で反映され、目標達成の瞬間を一緒に迎えられます。
                「自分の応援が形になった」という体験は、通常のクラファンでは得られない魅力です。
              </p>
              <p>
                <span class="bold-text">②支援購入した瞬間、推しの反応が見れる</span>
              </p>
              <p>
                ライブ配信だから支援購入した瞬間、推しの反応が見れるから、その場で応援が届いたことを実感できる。
              </p>
              <p>
                <span class="bold-text">③信頼感と透明性</span>
              </p>
              <p>
                配信で進捗や資金の使い道をその場で説明できるため、 支援者は安心して応援できます。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">まとめ：配信クラファンは「挑戦を一緒に叶える」時代へ</h2>
            <div class="learn-content">
              <p>
                配信クラファンは、ファンとクリエイターが共に挑戦を楽しむ新しいクラファンの形です。
                SukiSukiらいぶすてーしょんでは、面倒な準備をすべて運営がサポートし、グッズ制作や発送までを一括代行。
                配信者は「想い」と「素材」を用意するだけで、自分らしいクラファンを始められます。
              </p>
              <p>
                配信を通じて"夢をリアルに叶える"——それが、SukiSukiらいぶすてーしょんの配信クラファンです。
              </p>
            </div>
          </section>
        </v-container>
      </article>
    </main>

    <!-- CTA ボタン -->
    <CtaSection class="mb-10" article-id="academy-article-1" />
  </v-app>
</template>

<style scoped>
/* ヒーローセクション */
.hero-section {
  padding: 20px;
  width: 100%;
}

.hero-section img {
  width: 100%;
  height: auto;
  display: block;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-main-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.hero-left-content {
  flex: 1;
  text-align: left;
}

.hero-tag {
  background-color: #c8007f;
  color: white;
  padding: 8px 20px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 2.5rem;
  color: #6b4c93;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: #6b4c93;
  margin: 0;
}

.hero-right-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  max-width: 100%;
  height: auto;
}

/* パンくず */
.breadcrumb-section {
  background-color: white;
  /* padding: 15px 0; */
}

.breadcrumb {
  color: #666;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 8px;
  text-align: left;
  display: flex;
  list-style: none;
  margin: 0;
  gap: 8px;
}

.breadcrumb li {
  display: inline-block;
}

.breadcrumb li:not(:last-child)::after {
  content: ">";
  margin-left: 8px;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* メインコンテンツ */
::v-deep(.v-container) {
  text-align: left;
  padding: 0 !important;
}

.article-content {
  /* margin: 40px 0; */
  text-align: left;
}

.article-content h2,
.article-content h3 {
  text-align: left;
}

.article-title-box {
  margin-bottom: 5px;
}

.article-title-box h1,
.article-title-box h2 {
  color: #333;
  font-size: 18px;
  margin: 0;
  font-weight: bold;
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.6;
}

.update-date-box {
  padding: 10px 20px;
  margin-bottom: 10px;
}

.update-date-box p,
.update-date-box time {
  color: #333;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.intro-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  margin-left: 20px;
  margin-right: 20px;
  font-size: 14px;
}

.intro-box p {
  color: #333;
  margin: 0;
  line-height: 1.6;
  text-align: left;
}

.learn-section {
  margin: 30px 0;
}

.learn-title {
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 20px;
  text-align: left;
  background-color: #ffe0e0;
  padding: 15px 0 15px 20px;
  position: relative;
  line-height: 1.6;
}

.learn-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: #982da6;
}

.learn-content {
  margin-left: 20px;
  text-align: left;
}

.learn-content p {
  color: #333;
  line-height: 1.6;
  margin: 0 20px 15px 0;
  text-align: left;
  font-size: 16px;
}

/* CTA ボタン */
.cta-section {
  padding-bottom: 40px;
  margin-left: 40px;
  margin-right: 40px;
}

.cta-sectionnaka {
  /* padding-bottom: 40px; */
  margin-left: 20px;
  margin-right: 20px;
}

.cta-button {
  background: #da3990;
  color: white !important;
  font-size: 14px;
  font-weight: bold;
  height: 60px;
}

.custom-content {
  line-height: 1.6;
}

.bold-text {
  font-weight: bold;
}

/* 比較テーブル */
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}

.comparison-table th {
  background-color: #ffe0e0;
  font-weight: bold;
  color: #333;
}

.comparison-table td {
  background-color: #fff;
}

.comparison-table tr:hover {
  background-color: #f9f9f9;
}

.learn-content-table {
  margin-left: 20px;
  margin-right: 20px;
  text-align: left;
  line-height: 1.6;
}

@media (min-width: 750px) {
  .hero-section {
    padding: 0 25%;
  }

  .hero-section img {
    width: 100%;
    height: auto;
    display: block;
  }
  .breadcrumb {
    padding: 10px 25%;
  }
  .v-container {
    max-width: 5000px !important;
  }
  .article-title-box {
    padding: 0 25%;
  }
  .article-title-box h1,
  .article-title-box h2 {
    font-size: 20px;
    padding: 0;
    line-height: 1.6;
  }
  .update-date-box {
    padding: 10px 25%;
  }
  .update-date-box p,
  .update-date-box time {
    padding: 0;
  }
  .intro-box {
    padding: 10px;
  }
  .intro-box p {
    font-size: 16px;
    line-height: 1.6;
  }
  .intro-box {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-title {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content p {
    margin-left: 0;
    margin-right: 0;
  }
  .cta-section {
    margin-left: 30%;
    margin-right: 30%;
  }
  .cta-sectionnaka {
    margin-left: 30%;
    margin-right: 30%;
  }
  .learn-content-table {
    margin-left: 25%;
    margin-right: 25%;
    text-align: left;
  }
}
</style>
